import { create } from "zustand";
import type { ChatMessage, Conversation, ToolActivityItem, PropertyBlockData, CompanyBlockData, MunicipalityBlockData } from "@/types";

interface ChatState {
  // Current conversation
  conversationId: string | null;
  messages: ChatMessage[];
  isStreaming: boolean;
  activeTools: ToolActivityItem[];
  thinkingSteps: string[];

  // Conversation list
  conversations: Conversation[];
  pinnedIds: string[];

  // Disconnect recovery
  disconnectedWhileStreaming: boolean;
  lastUserMessage: string | null;
  pendingPropertyBlock: PropertyBlockData | null;
  pendingCompanyBlock: CompanyBlockData | null;

  // Actions
  setConversationId: (id: string | null) => void;
  addMessage: (message: ChatMessage) => void;
  appendToLastMessage: (content: string) => void;
  setLastMessageType: (type: "limit_reached") => void;
  setIsStreaming: (streaming: boolean) => void;
  addActiveTool: (toolName: string, context?: string) => void;
  completeActiveTool: (toolName: string, summary?: string) => void;
  failActiveTools: () => void;
  clearActiveTools: () => void;
  addThinkingStep: (text: string) => void;
  persistThinkingSteps: () => void;
  clearThinkingSteps: () => void;
  setConversations: (conversations: Conversation[]) => void;
  updateConversationTitle: (id: string, title: string) => void;
  loadConversation: (id: string, messages: ChatMessage[]) => void;
  setDisconnectedWhileStreaming: (val: boolean) => void;
  setLastUserMessage: (msg: string | null) => void;
  setPendingPropertyBlock: (data: PropertyBlockData | null) => void;
  pinConversation: (id: string) => void;
  unpinConversation: (id: string) => void;
  resetChat: () => void;
  updateLastMessageMapFeatures: (
    features: GeoJSON.FeatureCollection,
  ) => void;
  setLastMessagePropertyBlock: (data: PropertyBlockData) => void;
  setPendingCompanyBlock: (data: CompanyBlockData | null) => void;
  setLastMessageCompanyBlock: (data: CompanyBlockData) => void;
  setLastMessageMunicipalityBlock: (data: MunicipalityBlockData) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  conversationId: null,
  messages: [],
  isStreaming: false,
  activeTools: [],
  thinkingSteps: [],
  conversations: [],
  pinnedIds: JSON.parse(localStorage.getItem("ortvis-pinned") || "[]") as string[],
  disconnectedWhileStreaming: false,
  lastUserMessage: null,
  pendingPropertyBlock: null,
  pendingCompanyBlock: null,

  setConversationId: (id) => set({ conversationId: id }),

  addMessage: (message) =>
    set((state) => ({ messages: [...state.messages, message] })),

  appendToLastMessage: (content) =>
    set((state) => {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        messages[messages.length - 1] = {
          ...last,
          content: last.content + content,
        };
      }
      return { messages };
    }),

  setLastMessageType: (type) =>
    set((state) => {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        messages[messages.length - 1] = { ...last, type };
      }
      return { messages };
    }),

  setIsStreaming: (streaming) => set({ isStreaming: streaming }),

  addActiveTool: (toolName, context) =>
    set((state) => {
      // If context provided and a running tool with same name exists, update it (don't duplicate)
      if (context) {
        let updated = false;
        const tools = state.activeTools.map((t) => {
          if (!updated && t.name === toolName && t.status === "running" && !t.context) {
            updated = true;
            return { ...t, context };
          }
          return t;
        });
        if (updated) return { activeTools: tools };
      }
      return {
        activeTools: [
          ...state.activeTools,
          { name: toolName, status: "running" as const, startedAt: Date.now(), context },
        ],
      };
    }),

  completeActiveTool: (toolName, summary) =>
    set((state) => {
      let found = false;
      const tools: ToolActivityItem[] = state.activeTools.map((t) => {
        if (!found && t.name === toolName && t.status === "running") {
          found = true;
          return {
            name: t.name,
            status: "complete" as const,
            startedAt: t.startedAt,
            completedAt: Date.now(),
            summary,
          };
        }
        return t;
      });
      return { activeTools: tools };
    }),

  failActiveTools: () =>
    set((state) => ({
      activeTools: state.activeTools.map((t) =>
        t.status === "running"
          ? { ...t, status: "error" as const, completedAt: Date.now() }
          : t,
      ),
    })),

  clearActiveTools: () => set({ activeTools: [] }),

  addThinkingStep: (text) =>
    set((state) => ({ thinkingSteps: [...state.thinkingSteps, text] })),

  persistThinkingSteps: () =>
    set((state) => {
      if (state.thinkingSteps.length === 0) return {};
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        messages[messages.length - 1] = { ...last, thinkingSteps: [...state.thinkingSteps] };
      }
      return { messages };
    }),

  clearThinkingSteps: () => set({ thinkingSteps: [] }),

  setConversations: (conversations) => set({ conversations }),

  updateConversationTitle: (id, title) =>
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.conversation_id === id ? { ...c, title } : c,
      ),
    })),

  loadConversation: (id, messages) =>
    set({
      conversationId: id,
      messages,
      isStreaming: false,
      activeTools: [],
      disconnectedWhileStreaming: false,
      lastUserMessage: null,
    }),

  pinConversation: (id) =>
    set((state) => {
      const pinnedIds = [...state.pinnedIds, id];
      localStorage.setItem("ortvis-pinned", JSON.stringify(pinnedIds));
      return { pinnedIds };
    }),

  unpinConversation: (id) =>
    set((state) => {
      const pinnedIds = state.pinnedIds.filter((p) => p !== id);
      localStorage.setItem("ortvis-pinned", JSON.stringify(pinnedIds));
      return { pinnedIds };
    }),

  setDisconnectedWhileStreaming: (val) => set({ disconnectedWhileStreaming: val }),
  setLastUserMessage: (msg) => set({ lastUserMessage: msg }),
  setPendingPropertyBlock: (data) => set({ pendingPropertyBlock: data }),

  resetChat: () =>
    set({
      conversationId: null,
      messages: [],
      isStreaming: false,
      activeTools: [],
      disconnectedWhileStreaming: false,
      lastUserMessage: null,
      pendingPropertyBlock: null,
      pendingCompanyBlock: null,
    }),

  updateLastMessageMapFeatures: (features) =>
    set((state) => {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        // Merge with existing features (multiple tools send map_update in one turn)
        const existing = last.mapFeatures?.features ?? [];
        const incoming = features.features ?? [];
        const byKey = new Map<string, GeoJSON.Feature>();
        for (const f of existing) {
          const key = `${f.properties?.entity_id}:${f.geometry.type}`;
          byKey.set(key, f);
        }
        for (const f of incoming) {
          const key = `${f.properties?.entity_id}:${f.geometry.type}`;
          byKey.set(key, f);
        }
        messages[messages.length - 1] = {
          ...last,
          mapFeatures: { type: "FeatureCollection", features: Array.from(byKey.values()) },
        };
      }
      return { messages };
    }),
  setLastMessagePropertyBlock: (data) =>
    set((state) => {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        messages[messages.length - 1] = { ...last, propertyBlock: data };
      }
      return { messages };
    }),
  setPendingCompanyBlock: (data) => set({ pendingCompanyBlock: data }),
  setLastMessageCompanyBlock: (data) =>
    set((state) => {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        messages[messages.length - 1] = { ...last, companyBlock: data };
      }
      return { messages };
    }),
  setLastMessageMunicipalityBlock: (data) =>
    set((state) => {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant") {
        messages[messages.length - 1] = { ...last, municipalityBlock: data };
      }
      return { messages };
    }),
}));
